// What happened at an activation, newest first, as values this console can draw.
//
// attendees.ts opens with the rule that governs this file too, and this read
// carries more of those sentences than any other on the console: `subset_note`,
// `attribution_note`, `access_note`, the `note` on the retention block and every
// `cannot_answer` entry travel through here untouched. xp/operator/src/
// activity.ts calls them "sentences to print verbatim, not summarise" and each
// one exists because the absence it describes draws as a reassuring answer. A
// feed missing `subset_note` is a feed a reader takes for the whole record of a
// show; missing `attribution_note`, it is a feed whose blank actor cells read as
// nobody having acted.
//
// So they are required in the strong sense: a response missing any of them is a
// response this console will not draw at all.
//
// ---------------------------------------------------------------------------
// THE ATTRIBUTION IS A TAGGED UNION AND AN UNRECOGNISED ARM IS REFUSED, WHICH IS
// THE ONE DECISION IN THIS FILE WORTH ARGUING FOR
//
// The service's own header says the three states are not each other: `withheld`
// is this reader not being entitled to a value that exists, `none` is a row that
// records a machine where there was never a person to be entitled to, and
// `recorded` is the answer. Collapsing any pair of them is a lie a reader would
// act on, and the specific one 131 names is a capture row's blank cell telling a
// client that nobody ran their export.
//
// Everywhere else in this package an unrecognised word is drawn as itself, on
// People's rule that a channel this console has no label for is still a fact.
// That rule is right for a label and wrong for a tag: there is no honest way to
// draw a fourth arm, because what a screen does with this value is choose which
// of three different sentences to print, and the conservative choice would be
// picking one of them for a state the service did not mean. So a line whose
// attribution this file does not recognise makes the whole page unreadable,
// which is loud, rather than one cell wrong, which is silent.
//
// ---------------------------------------------------------------------------
// AND NOTHING IS DERIVED HERE
//
// `actor` arrives composed. The service turns `actor_kind`, `actor_role`,
// `actor_credential` and `actor_ref` into the one phrase the design's fourth
// column draws, in a function that has 011's and 018's reasoning in it about
// what a shared token means and why a web row has no device. A console that
// assembled that phrase itself would be a second opinion about who acted, and it
// would be the opinion nobody checked.
//
// No policy about who may read this either, and here that is the substance
// rather than the usual disclaimer. This endpoint answers a client viewer with a
// real page: 131 grants `activity_feed` to both roles and withholds the
// attribution from one of them by privilege. Unverified.tsx refuses a client
// viewer locally because that endpoint is Nova-only and its refusal sentence is
// knowingly the wrong one; neither is true here, so nothing in this file or on
// its screen tests a role.

import type { ActivitySortKey } from "../../../operator/src/activity.js";
import { activityUrl } from "./endpoints.js";
import { authenticated, failureFor, type ApiOptions, type Read } from "./client.js";
import type { SessionToken } from "../session/store.js";

/**
 * How many recorded acts this console asks for at a time.
 *
 * Its own number rather than `ACTIVITY_PAGE_DEFAULT` from xp/operator, on
 * attendees.ts's reasoning about importing a default: the page is the unit
 * `report_access` counts this disclosure in, so the figure filed against
 * somebody's sign-in would otherwise change without a screen changing.
 *
 * Fifty, which is larger than the twenty five the two people screens ask for,
 * and the difference is about what a page of each one is. A page of People is
 * twenty five named people with their addresses on it. A page of this is fifty
 * recorded acts naming nobody: the rows about a person carry the pseudonym that
 * activation's own records use, and a log read fifty lines at a time is a log
 * somebody can follow rather than one they turn.
 *
 * No control for it on the screen, unlike the two paged reads next door. Their
 * pagers offer three sizes because the shared pager does; a keyset page has no
 * pager to hang the choice on, and a size somebody can change is a size the
 * audit row records differently on every page of one sitting.
 */
export const ACTIVITY_PER_PAGE = 50;

/** A fact the design asks for and this service does not hold, in the service's words. */
export type ActivityGap = {
  readonly what: string;
  readonly why: string;
};

/**
 * Who acted, where this reader may be told and where anybody could be.
 *
 * The service's three arms, kept as three. See the header for why a fourth is
 * refused rather than drawn conservatively.
 */
export type ActivityAttribution =
  /** A client viewer. 129's four columns are not theirs, and the page says why. */
  | { readonly kind: "withheld" }
  /** A capture row. There was never a person to name, which is 001's design. */
  | { readonly kind: "none" }
  | {
      readonly kind: "recorded";
      /** The person 011 proved, or null on a credential that names nobody. */
      readonly identityId: string | null;
      /** What somebody typed where nothing could check it. Never beside a proof. */
      readonly claimed: string | null;
      /** Where the request came from, or null on a row that records no address. */
      readonly sourceAddress: string | null;
    };

/**
 * One recorded act.
 *
 * NARROWER THAN THE SERVICE'S LINE BY FOUR FIELDS, and each is dropped for a
 * reason rather than for brevity, because a field carried and not drawn is a
 * field somebody draws later without reading why it was there.
 *
 *   `spine_seq` is the keyset's tiebreak for the one arm that has a server-side
 *   order. 131's header says there is deliberately no cross-source equivalent,
 *   so a column of it would be a number present on most rows and absent on the
 *   rest for a reason no reader could work out from the screen.
 *
 *   `actor_kind` and `actor_role` are the working behind `actor`, which arrives
 *   composed. Drawing either beside the phrase built out of it is drawing the
 *   same fact twice, and the second copy is the one with no sentence attached.
 *
 *   `actor_ref` is the whole station or device uuid, and `actor` already carries
 *   the first eight characters of it. The service shortened it on the argument
 *   that eight is what a person reads off a screen and compares, and put the
 *   whole value on the line for a caller matching it against something else.
 *   Drawing the other twenty-eight beside the phrase would be this console
 *   overruling that decision in the one cell it was made about.
 *
 * `actor_credential` is kept, and it is the one of the four that is not already
 * inside `actor` on every row: on a capture row it is 018's channel, so it is
 * the difference between a row a hardware keystore signed and a row that records
 * having no signature and why. The service only folds it into the phrase on the
 * rows where nothing else identifies the actor.
 */
export type ActivityLine = {
  /** 131's key, which is also the cursor. Carries its source and its source's own key. */
  readonly activityId: string;
  readonly occurredAt: string;
  readonly action: string;
  readonly entity: string;
  /**
   * The attendee reference, the station, the voided event or the file.
   *
   * A pseudonym on the rows that are about a person, never a name and never an
   * address. 131 refuses to put one here and the operator module does not put
   * one back.
   */
  readonly entityId: string | null;
  /** Which of 131's three arms this row came from. */
  readonly source: string;
  /** The service's phrase for what acted. Composed there, never here. */
  readonly actor: string;
  /** How it was proved: a capture channel on the spine, 011's credential elsewhere. */
  readonly actorCredential: string | null;
  readonly attribution: ActivityAttribution;
  /** What it touched, in vocabulary rather than in anything a person typed. */
  readonly detail: string | null;
};

/**
 * Where this page sits.
 *
 * NO `total` AND NO `offset`, WHICH IS NOT AN OMISSION TO BE FILLED IN. The
 * service's own type says why: this is a union that is appended to while
 * somebody reads it, so an offset skips and repeats rows with no error anywhere,
 * and a total would cost a second pass over the whole union on every page to
 * produce a number that is stale before it is drawn. What replaces it is `more`,
 * which costs one extra row.
 *
 * So there is nothing here to build a page count or a "showing X of Y" out of,
 * and the screen must not invent one.
 */
export type ActivityPagePosition = {
  readonly limit: number;
  readonly returned: number;
  readonly more: boolean;
  /** The cursor this page was read from, or null on the first page. */
  readonly from: string | null;
  /** The cursor for the next page, or null where this page is the end. */
  readonly next: string | null;
};

/** What the caller narrowed the read to, echoed back so the screen can say so. */
export type ActivityFilters = {
  readonly action: string | null;
  readonly entity: string | null;
};

/**
 * When this feed goes, and the sentence saying that nothing makes it go.
 *
 * `note` required and the two facts not, which is `asRetention`'s split in
 * unverifiedAttempts.ts and it lands harder here. There the sentence says when
 * rows are removed, because something removes them. Here it says the opposite:
 * the agreed period is what was agreed rather than what is enforced, because
 * nothing in this service deletes any of the three sources. That is the specific
 * claim the design's footer gets wrong, so the sentence is the answer and the
 * two values beside it are the working.
 */
export type ActivityRetention = {
  readonly sealedAt: string | null;
  readonly retentionMonths: number | null;
  readonly note: string;
};

export type ActivityFeedPage = {
  readonly activationId: string;
  readonly asAt: string;
  readonly accessNote: string;
  readonly events: readonly ActivityLine[];
  readonly page: ActivityPagePosition;
  readonly filters: ActivityFilters;
  readonly retention: ActivityRetention;
  /** What is in the feed and what is not, for whoever reads it as a complete record. */
  readonly subsetNote: string;
  /** Why a person is named on some rows and not on others, said once. */
  readonly attributionNote: string;
  readonly cannotAnswer: readonly ActivityGap[];
};

/**
 * One page of what happened at an activation.
 *
 * The page size and the cursor are arguments rather than state held here, which
 * is `readAttendees`'s shape and its reason: the size on the wire and the size
 * in the `report_access` row this read files are the same number, and the cursor
 * belongs to whoever is walking the list.
 *
 * BOTH FILTERS GO TO THE SERVICE AND NEITHER IS APPLIED HERE, which is the one
 * place this read differs from People's search box. There the filter is over
 * rows already delivered, on Choose.tsx's rule that narrowing what is already on
 * the screen discloses nothing new. Here the vocabulary is closed and the
 * service refuses a value outside it with a 400 that names the vocabulary, so a
 * filter applied in a browser would turn a typo into an empty page — and an
 * empty page is also what a quiet activation looks like, which is the confusion
 * the service went to the trouble of refusing.
 */
export async function readActivity(
  options: ApiOptions,
  token: SessionToken,
  activationId: string,
  filters: { cursor?: string | null; action?: string | null; entity?: string | null } = {},
  limit: number = ACTIVITY_PER_PAGE,
  /**
   * The column and direction the reader pressed, or null for newest first. The
   * service orders the whole feed and its cursor walks that order, so a cursor
   * from one order is refused in another rather than read part way down it.
   */
  order: { readonly key: ActivitySortKey; readonly dir: "asc" | "desc" } | null = null,
): Promise<Read<ActivityFeedPage>> {
  const answer = await authenticated(options, token, {
    path: activityUrl(activationId, limit, filters, order),
    method: "GET",
  });
  if (!answer.ok) return { ok: false, ...failureFor(answer) };

  const raw = fields(answer.body);
  const rows = raw["events"];
  const position = asPagePosition(raw["page"], limit);
  const retention = asRetention(raw["retention"]);
  const subsetNote = text(raw["subset_note"]);
  const attributionNote = text(raw["attribution_note"]);
  const accessNote = text(raw["access_note"]);
  const gaps = asGaps(raw["cannot_answer"]);

  // ALL FIVE SENTENCES ARE REQUIRED, and `attribution_note` is the one worth
  // naming beside `subset_note`. It is the sentence saying why a person is named
  // on some rows and not others, and the service sends a different one to each
  // audience: a client viewer is told what is withheld and that Nova holds it,
  // and Nova is told that a shared token names nobody. Lose it and the actor
  // column is a column of phrases with nothing on the page explaining why some
  // of them are people and some of them are tablets.
  if (
    !Array.isArray(rows) ||
    position === null ||
    retention === null ||
    subsetNote === null ||
    attributionNote === null ||
    accessNote === null ||
    gaps === null
  ) {
    return { ok: false, ...unreadable() };
  }

  const events: ActivityLine[] = [];
  for (const entry of rows) {
    const line = asLine(entry);
    if (line === null) return { ok: false, ...unreadable() };
    events.push(line);
  }

  // `returned` AND THE ROWS ARE ONE FACT SENT TWICE, SO A DISAGREEMENT IS NOT A
  // CHOICE THIS CONSOLE GETS TO MAKE. The service takes `returned` off the same
  // result set it serialises, which means the two cannot differ on any response
  // it produces, and the standing rule against recomputing what the service
  // computed says which one the pager draws. What the rule does not settle is
  // what to do if they disagree anyway, and the answer is neither: `returned` is
  // the figure filed in `report_access.row_count` as the size of this
  // disclosure, so a page that hands over four rows while recording five is a
  // response whose audit trail this console cannot vouch for, and drawing
  // either number would be picking one and saying nothing.
  if (position.returned !== events.length) return { ok: false, ...unreadable() };

  return {
    ok: true,
    value: {
      activationId: text(raw["activation_id"]) ?? activationId,
      asAt: text(raw["as_at"]) ?? "",
      accessNote,
      events,
      page: position,
      filters: asFilters(raw["filters"]),
      retention,
      subsetNote,
      attributionNote,
      cannotAnswer: gaps,
    },
  };
}

// ------------------------------------------------------------------ the shapes

/**
 * One recorded act, or the fact that this is not one.
 *
 * Six values are required and the rest may be null. `activity_id` is what makes
 * a row identifiable and is the cursor; `occurred_at` is what places it in the
 * show; `action` and `entity` are what the row says happened; `source` is what
 * tells two records of one export apart, which 131 keeps a column for on exactly
 * that ground; and `actor` is the answer to the design's fourth column.
 *
 * The attribution is required and is refused rather than defaulted. See the
 * header: there is no honest fourth way to draw it.
 */
function asLine(value: unknown): ActivityLine | null {
  const row = fields(value);
  const activityId = text(row["activity_id"]);
  const occurredAt = text(row["occurred_at"]);
  const action = text(row["action"]);
  const entity = text(row["entity"]);
  const source = text(row["source"]);
  const actor = text(row["actor"]);
  const attribution = asAttribution(row["attribution"]);

  if (
    activityId === null ||
    occurredAt === null ||
    action === null ||
    entity === null ||
    source === null ||
    actor === null ||
    attribution === null
  ) {
    return null;
  }

  return {
    activityId,
    occurredAt,
    action,
    entity,
    entityId: text(row["entity_id"]),
    source,
    actor,
    actorCredential: text(row["actor_credential"]),
    attribution,
    detail: text(row["detail"]),
  };
}

/**
 * The union tag, checked against the three the service writes.
 *
 * NULL FOR ANYTHING ELSE, WHICH TAKES THE WHOLE PAGE DOWN. The three fields
 * inside `recorded` are each nullable and that is the service's design rather
 * than a tolerance here: a shared admin token is a `recorded` attribution with
 * no identity to name, and a published configuration is one with a typed name
 * and no address. Those nulls are answers. What is not an answer is a tag this
 * file has never heard of, because the only thing a screen does with the tag is
 * decide which of three sentences to print.
 */
function asAttribution(value: unknown): ActivityAttribution | null {
  const row = fields(value);
  const kind = row["kind"];
  if (kind === "withheld") return { kind: "withheld" };
  if (kind === "none") return { kind: "none" };
  if (kind === "recorded") {
    return {
      kind: "recorded",
      identityId: text(row["identity_id"]),
      claimed: text(row["claimed"]),
      sourceAddress: text(row["source_address"]),
    };
  }
  return null;
}

/**
 * Where the page sits, or the fact that the response did not say.
 *
 * `returned` IS THE ONLY FIGURE REQUIRED, and `more` is read as a boolean rather
 * than defaulted from anything. A response whose `more` was lost draws as the
 * end of the list, which is the reassuring reading and the wrong one: somebody
 * walking a client's activity would stop one page in and believe they had seen
 * it all. So it has to be `true` on the wire, and `next` has to be a cursor,
 * before this console offers another page.
 *
 * `limit` falls back to what was asked for, which cannot be wrong: the request
 * carried it.
 */
function asPagePosition(value: unknown, limit: number): ActivityPagePosition | null {
  const row = fields(value);
  const returned = integer(row["returned"]);
  if (returned === null) return null;
  return {
    limit: integer(row["limit"]) ?? limit,
    returned,
    more: row["more"] === true,
    from: text(row["from"]),
    next: text(row["next"]),
  };
}

/**
 * What the service says it narrowed to.
 *
 * Not required, and not the console's own state either. The screen holds what
 * somebody chose and this is what the service confirms it applied, and the two
 * being separate is what lets the screen say which rows are on it: a filter the
 * service echoes back as null is a filter it did not apply.
 */
function asFilters(value: unknown): ActivityFilters {
  const row = fields(value);
  return { action: text(row["action"]), entity: text(row["entity"]) };
}

function asRetention(value: unknown): ActivityRetention | null {
  const row = fields(value);
  const note = text(row["note"]);
  if (note === null) return null;
  return { sealedAt: text(row["sealed_at"]), retentionMonths: integer(row["retention_months"]), note };
}

/**
 * The gap list, or the fact that the response did not carry one.
 *
 * Null and not `[]`, which is `asGaps`'s reasoning in attendees.ts. An empty
 * array is a statement, and the statement is that this feed answers everything
 * the design asked of an audit log. Seven of its entries say otherwise, three of
 * them about passcode actions the design puts in its own filter.
 */
function asGaps(value: unknown): readonly ActivityGap[] | null {
  if (!Array.isArray(value)) return null;
  const gaps: ActivityGap[] = [];
  for (const entry of value) {
    const row = fields(entry);
    const what = text(row["what"]);
    const why = text(row["why"]);
    if (what === null || why === null) return null;
    gaps.push({ what, why });
  }
  return gaps;
}

// -------------------------------------------------------------- small things

/**
 * The refusal for an answer this file could not read.
 *
 * The second sentence is this read's own. A feed drawn from half an answer is a
 * partial record of a show presented as the record of a show, and the reader most
 * likely to be looking at it is somebody checking what happened at their own
 * activation.
 */
function unreadable(): { readonly kind: "unavailable"; readonly detail: string } {
  return {
    kind: "unavailable",
    detail:
      "The service answered with activity this console could not read. Nothing is drawn from it. A row " +
      "here means nothing without the sentences the service sends beside it saying what the feed carries " +
      "and who it names, so a response missing any of them is not drawn in part.",
  };
}

function fields(value: unknown): Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value) ? (value as Record<string, unknown>) : {};
}

/** A string, or nothing. Never the empty string, which a screen reads as a value. */
function text(value: unknown): string | null {
  return typeof value === "string" && value !== "" ? value : null;
}

/**
 * A whole number the service actually sent.
 *
 * `typeof value === "number"` first, for the reason attendees.ts gives:
 * `Number(null)` is zero and finite, so the shorter version turns an absent
 * count into a report that nothing happened. Here that would be a page reading
 * nought rows above a table with rows in it.
 */
function integer(value: unknown): number | null {
  if (typeof value === "number") return Number.isSafeInteger(value) ? value : null;
  if (typeof value !== "string" || value.trim() === "") return null;
  const parsed = Number(value);
  return Number.isSafeInteger(parsed) ? parsed : null;
}
